import { CaseData } from "@/cms/models";
import PriceDetail from "../PriceDetails";
import TextDetails from "../TextDetails";
import ExpenseDetail from "../ExpenseDetails";
import ConnectionFeesDetails from "../ConnectionFeesDetails";
import {
  formatDate,
  getCaseClassification,
  isRental,
  isOmfattetAfLofe,
  shouldShowOwnerExpenses,
} from "@/utils/helper";
import ExpensePercentageDetails from "../ExpensePercentageDetails";

const FullFinancialFacts: React.FC<{
  caseData: CaseData;
  showStandardFinancing: boolean;
}> = ({ caseData, showStandardFinancing }) => {
  const caseClassification = getCaseClassification(caseData);
  const isRent = isRental(caseClassification);
  const isOmfattetAfLofeValue = isOmfattetAfLofe(caseClassification);
  const showOwnerExpenses = shouldShowOwnerExpenses(caseData);
  return (
    <div className="p-4 bg-[var(--box-bg-color)] text-[var(--box-text-color)] homepage-text-sm">
      {caseData.priceInfo && !isRent && (
        <>
          <PriceDetail label="Pris" value={caseData.priceInfo ?? 0} />
        </>
      )}
      {caseData.connectionFeesPaid && (
        <ConnectionFeesDetails
          label="Tilslutningsafgifter betalt"
          value={caseData.connectionFeesPaid}
        />
      )}
      {caseData.rentalTransferPrice > 0 && (
        <ExpenseDetail
          label="Afståelsessum"
          value={caseData.rentalTransferPrice}
        />
      )}
      {showStandardFinancing &&
        caseData.downPayment > 0 &&
        caseData.downPayment !== caseData.price && (
          <ExpenseDetail label="Udbetaling" value={caseData.downPayment} />
        )}
      {showStandardFinancing && caseData.totalMonthlyGrossPayment > 0 && (
        <ExpenseDetail
          label="Brutto (ex. ejerudgifter)"
          value={caseData.totalMonthlyGrossPayment}
        />
      )}
      {showStandardFinancing && caseData.totalMonthlyNetPayment > 0 && (
        <ExpenseDetail
          label="Netto (ex. ejerudgifter)"
          value={caseData.totalMonthlyNetPayment}
        />
      )}

      {showOwnerExpenses && (
        <ExpenseDetail
          label="Ejerudgifter pr. md."
          value={caseData.ownerExpensesTotalMonthly}
        />
      )}
      {caseData.rentMonthly > 0 && (
        <ExpenseDetail label=" Leje pr. md." value={caseData.rentMonthly} />
      )}
      {caseData.rentAnnually > 0 && (
        <ExpenseDetail label=" Leje pr. år" value={caseData.rentAnnually} />
      )}
      {caseData.rentM2Annually > 0 && (
        <ExpenseDetail
          label=" Leje pr. m2 pr. år"
          value={caseData.rentM2Annually}
        />
      )}
      {caseData.rentAvailableFrom && (
        <TextDetails
          label="Ledig fra"
          value={formatDate(caseData.rentAvailableFrom)}
        />
      )}
      {caseData.rentAvailableTo && (
        <TextDetails
          label="Ledig til"
          value={formatDate(caseData.rentAvailableTo)}
        />
      )}
      {caseData.maxRentalPeriod > 0 && (
        <TextDetails
          label="Maksimal udlejningsperiode (mdr.)"
          value={caseData.maxRentalPeriod.toFixed(0) || ""}
        />
      )}
      {caseData.commonCostsAnnually > 0 && (
        <ExpenseDetail
          label="Fællesudgifter"
          value={caseData.commonCostsAnnually}
        />
      )}
      {caseData.consumptionCostsAnnually > 0 && (
        <ExpenseDetail
          label="Forbrugsudgifter"
          value={caseData.consumptionCostsAnnually}
        />
      )}
      {caseData.deposit > 0 && (
        <ExpenseDetail label="Depositum" value={caseData.deposit} />
      )}
      {caseData.prepaidRent > 0 && (
        <ExpenseDetail label="Forudbetalt leje" value={caseData.prepaidRent} />
      )}
      {caseData.consumptionCostsMonthly > 0 && (
        <ExpenseDetail
          label="A/C-forbrug pr. md."
          value={caseData.consumptionCostsMonthly}
        />
      )}
      {caseData.coopChargeMonthly > 0 && (
        <ExpenseDetail
          label="Boligydelse pr. md."
          value={caseData.coopChargeMonthly}
        />
      )}
      {caseData.heatingCosts > 0 && (
        <ExpenseDetail
          label="Varmeudgift pr. md."
          value={caseData.heatingCosts}
        />
      )}
      {caseData.operatingCostsAnnually > 0 && !isOmfattetAfLofeValue && (
        <ExpenseDetail
          label="Årlige driftsudgifter"
          value={caseData.operatingCostsAnnually}
        />
      )}
      {caseData.rentalIncomeAnnually > 0 && (
        <ExpenseDetail
          label="Lejeindtægter"
          value={caseData.rentalIncomeAnnually}
        />
      )}
      {caseData.rentalIncomeM2Annually > 0 && (
        <ExpenseDetail
          label="Lejeindtægter pr. m2"
          value={caseData.rentalIncomeM2Annually}
        />
      )}
      {caseData.rateOfReturn > 0 && (
        <ExpensePercentageDetails
          label="Afkast i %"
          value={caseData.rateOfReturn}
        />
      )}
      {caseData.depreciation && (
        <div className="flex justify-between">
          <div className="flex items-center gap-1 propert-label">
            <span>Afskrivningsberettiget</span>
          </div>
          <div className="homepage-text-sm ">
            <span>
              {caseData.depreciation ? <span>Ja</span> : <span>Nej</span>}
            </span>
          </div>
        </div>
      )}
      {caseData.vatRegistered && (
        <div className="flex justify-between">
          <div className="flex items-center gap-1 propert-label">
            <span>Moms</span>
          </div>
          <div className="homepage-text-sm ">
            <span>
              {caseData.vatRegistered ? <span>Ja</span> : <span>Nej</span>}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FullFinancialFacts;
